import {FC, useState, useEffect} from 'react'
import {useFieldArray, useForm} from 'react-hook-form'
import {yupResolver} from '@hookform/resolvers/yup'
import {Modal} from 'jbc-front/components/presenters/ui/Modal'
import Button from 'jbc-front/components/Button'
import {ButtonRow} from 'jbc-front/components/presenters/layout/ButtonRow'
import * as FileDrop from 'jbc-front/components/presenters/form/FileDrop'
import type {ProcedureStatus} from '../../query'
import {Tabs} from './Tabs'
import {Tab} from './Tab'
import {Schema, schema} from './schema'
import styles from './FilePickerModal.scss'

const isProcedureAttachmentSupported = (procedureStatus: ProcedureStatus) => {
  return [
    'start_childcare',
    'extend_childcare',
    'finish_childcare',
    'additional_childcare_leave_allowance',
    'parental_leave',
    'parental_leave_support_allowance',
  ].includes(procedureStatus.procedureType)
}

export type PickedFile = PickedProcedureAttachment | UploadFile

export interface PickedProcedureAttachment {
  type: 'ProcedureAttachment'
  id: string
  file: {
    filename: string
    url: string
  }
}

export interface UploadFile {
  type: 'UploadFile'
  file: File
}

interface FilePickerModalProps {
  procedureStatus: ProcedureStatus
  isOpen: boolean
  onClose: () => void
  onSubmit: (files: PickedFile[]) => void
}

export const FilePickerModal: FC<FilePickerModalProps> = ({procedureStatus, isOpen, onClose, onSubmit}) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalContent procedureStatus={procedureStatus} onClose={onClose} onSubmit={onSubmit} />
    </Modal>
  )
}

interface ModalContentProps {
  procedureStatus: ProcedureStatus
  onClose: () => void
  onSubmit: (files: PickedFile[]) => void
}

const ModalContent: FC<ModalContentProps> = ({procedureStatus, onClose, onSubmit}) => {
  const [currentTab, setCurrentTab] = useState<'attachment' | 'upload'>(
    isProcedureAttachmentSupported(procedureStatus) ? 'attachment' : 'upload'
  )
  const {
    handleSubmit,
    control,
    register,
    reset,
    watch,
    formState: {errors},
  } = useForm<Schema>({
    resolver: yupResolver(schema),
    defaultValues: {
      attachments: [],
      uploads: [],
    },
  })
  const attachments = watch('attachments')
  const uploads = useFieldArray({control, name: 'uploads'})

  useEffect(() => {
    reset()
  }, [currentTab, reset])

  const submit = (data: Schema) => {
    switch (currentTab) {
      case 'attachment': {
        const files = procedureStatus.procedureAttachments
          .filter((attach) => data.attachments.includes(attach.id))
          .map((attach) => ({...attach, type: 'ProcedureAttachment' as const}))
        return onSubmit(files)
      }
      case 'upload': {
        const files = data.uploads.map((upload) => ({...upload, type: 'UploadFile' as const}))
        return onSubmit(files)
      }
      default:
        return currentTab satisfies never // exhaustiveness check
    }
  }

  return (
    <>
      <Modal.Header onClose={onClose}>ファイルを選択</Modal.Header>
      <Modal.Body className={styles.modalBody}>
        {isProcedureAttachmentSupported(procedureStatus) && (
          <Tabs>
            <Tab
              label="添付ファイルから選択"
              active={currentTab === 'attachment'}
              onClick={() => setCurrentTab('attachment')}
            />
            <Tab label="PCからアップロード" active={currentTab === 'upload'} onClick={() => setCurrentTab('upload')} />
          </Tabs>
        )}

        {currentTab === 'attachment' && (
          <div className={styles.tabContent}>
            {procedureStatus.procedureAttachments.length === 0 ? (
              <div className={styles.attachmentsEmpty}>この手続きには添付ファイルがありません</div>
            ) : (
              <>
                <div className={styles.attachmentsHeader}>ファイル一覧</div>
                <ul className={styles.attachmentsList}>
                  {procedureStatus.procedureAttachments.map(({id, file: {filename}}) => (
                    <li key={id}>
                      <label className={styles.attachmentFile}>
                        <input className="m-checkbox-input" type="checkbox" {...register('attachments')} value={id} />
                        <span className={styles.attachmentFileName}>{filename}</span>
                      </label>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        )}

        {currentTab === 'upload' && (
          <div className={styles.tabContent}>
            <FileDrop.Dropzone
              multiple
              maxFiles={10}
              onDrop={(files) => uploads.append(files.map((file) => ({file})))}
              hint="1ファイル最大アップロードサイズ：10MB"
            />
            {uploads.fields.length > 0 && (
              <>
                <FileDrop.FilesHeader>アップロードファイル</FileDrop.FilesHeader>
                <FileDrop.Files>
                  {uploads.fields.map((upload, index) => (
                    <div key={upload.id}>
                      <FileDrop.File file={upload.file} onRemove={() => uploads.remove(index)} />
                      {errors.uploads?.[index]?.file && (
                        <div className={styles.fileError}>{errors.uploads[index].file.message}</div>
                      )}
                    </div>
                  ))}
                </FileDrop.Files>
              </>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <ButtonRow>
          <Button onClick={onClose}>キャンセル</Button>
          <Button
            primary
            disabled={attachments.length === 0 && uploads.fields.length === 0}
            onClick={handleSubmit(submit)}
          >
            確定
          </Button>
        </ButtonRow>
      </Modal.Footer>
    </>
  )
}
